import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { of, Observable } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { Oauth } from '../../models/appcloud/oauth.model';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { environment } from '../../environments/environment';
import { HttpHeaderConstants } from '../../services/http-header.constant';

@Injectable({
    providedIn: 'root',
})
export class OauthService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private OAUTHS_URL: string = environment.publicApiHost + '/appcloud/oauths';

    get(id: number, additionalHeaders: any = {}): Observable<Oauth> {
        this.#spinnerService.addProcess('OauthService.get.' + id, 'Retrieving your authentication information...');

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.get<Oauth>(this.OAUTHS_URL + '/' + id, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve authorization data. Please try again later.'
                );
                return of<Oauth>();
            }),
            finalize(() => this.#spinnerService.completeProcess('OauthService.get.' + id))
        );
    }

    findByType(type: string, additionalHeaders: any = {}): Observable<Oauth[]> {
        this.#spinnerService.addProcess(
            'OauthService.findByType.' + type,
            'Locating your authentication information...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('type', type),
        };
        return this.#http.get<Oauth[]>(this.OAUTHS_URL, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve authorization data. Please try again later.'
                );
                return of<Oauth[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess('OauthService.findByType.' + type))
        );
    }

    create(newOauth: Oauth, additionalHeaders: any = {}): Observable<Oauth> {
        this.#spinnerService.addProcess(
            'OauthService.create.' + newOauth.type,
            'Saving your authentication information...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.post<Oauth>(this.OAUTHS_URL, newOauth, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while saving authorization data. Please try again later.'
                );
                return of<Oauth>();
            }),
            finalize(() => this.#spinnerService.completeProcess('OauthService.create.' + newOauth.type))
        );
    }

    update(id: number, oauth: Oauth, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess('OauthService.update.' + id, 'Updating your authentication information...');

        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http.patch<Oauth>(this.OAUTHS_URL + '/' + id, oauth, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while sending data to Relationship One. Please try again later.'
                );
                return of<Oauth>();
            }),
            finalize(() => this.#spinnerService.completeProcess('OauthService.update.' + id))
        );
    }
}
